/**
 * PATH: packages/email-hub/src/feature/components/InlineImageMenu.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-INLINEIMAGEMENU-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/InlineImageMenu.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/InlineImageMenu.tsx:
 *     thin re-export shim that points back here.
 */

import { useNavigate } from 'react-router-dom'
import {
  Copy, Download, ExternalLink, Forward, Maximize2, Pen, Reply,
} from 'lucide-react'
import {
  ContextMenu,
  ContextMenuHeader,
  ContextMenuItem,
  ContextMenuSeparator,
} from '../../_internal/components/ContextMenu'
import { useToast } from '../../_internal/components/Toast'
import {
  blobToFile,
  defaultFilenameFor,
  fetchImageBlob,
  writeImageToClipboard,
} from '@quotech/email-hub/feature/lib/imageClipboard'
import {
  composeUrlFor,
  useComposePendingStore,
  type ComposeIntent,
} from '../stores/composePendingStore'

export interface InlineImageMenuState {
  x: number
  y: number
  src: string
  alt?: string
  /** Email this image is hosted in. ``null`` for the global
   *  shortcut path where there is no anchor email — Reply/Forward
   *  items are hidden in that case. */
  emailId: string | null
}

export interface InlineImageMenuProps {
  state: InlineImageMenuState | null
  onClose: () => void
  onOpenLightbox: (src: string, alt?: string) => void
  onOpenInMarkup: (src: string) => void
}

export function InlineImageMenu({
  state, onClose, onOpenLightbox, onOpenInMarkup,
}: InlineImageMenuProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const setPending = useComposePendingStore((s) => s.setPending)

  if (!state) return null

  const filename = filenameFromSrc(state.src, state.alt)

  async function handleCopy() {
    if (!state) return
    const blob = await fetchImageBlob(state.src)
    if (!blob) {
      toast('Could not load image bytes to copy', 'error')
      return
    }
    const ok = await writeImageToClipboard(blob)
    toast(
      ok ? 'Image copied to clipboard' : 'Browser blocked clipboard write',
      ok ? 'success' : 'error',
    )
    onClose()
  }

  async function handleDownload() {
    if (!state) return
    const blob = await fetchImageBlob(state.src)
    if (!blob) {
      toast('Could not download image', 'error')
      return
    }
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
    onClose()
  }

  async function handlePushToCompose(intent: ComposeIntent) {
    if (!state) return
    const blob = await fetchImageBlob(state.src)
    if (!blob) {
      toast('Could not attach image', 'error')
      return
    }
    setPending({ files: [blobToFile(blob, filename)], intent })
    navigate(composeUrlFor(intent))
    onClose()
  }

  return (
    <ContextMenu x={state.x} y={state.y} onClose={onClose}>
      <ContextMenuHeader>{state.alt || 'Image'}</ContextMenuHeader>
      <ContextMenuItem
        icon={Maximize2}
        onClick={() => {
          onOpenLightbox(state.src, state.alt)
          onClose()
        }}
      >
        Open larger
      </ContextMenuItem>
      <ContextMenuItem icon={Copy} onClick={handleCopy}>
        Copy image
      </ContextMenuItem>
      <ContextMenuItem icon={Download} onClick={handleDownload}>
        Save image
      </ContextMenuItem>
      <ContextMenuItem
        icon={ExternalLink}
        onClick={() => {
          window.open(state.src, '_blank', 'noopener,noreferrer')
          onClose()
        }}
      >
        Open in new tab
      </ContextMenuItem>
      <ContextMenuSeparator />
      {state.emailId ? (
        <>
          <ContextMenuItem
            icon={Reply}
            onClick={() =>
              handlePushToCompose({ kind: 'reply', emailId: state.emailId! })
            }
          >
            Reply with this image
          </ContextMenuItem>
          <ContextMenuItem
            icon={Forward}
            onClick={() =>
              handlePushToCompose({ kind: 'forward', emailId: state.emailId! })
            }
          >
            Forward with this image
          </ContextMenuItem>
        </>
      ) : (
        <ContextMenuItem
          icon={Reply}
          onClick={() => handlePushToCompose({ kind: 'new' })}
        >
          New email with this image
        </ContextMenuItem>
      )}
      <ContextMenuSeparator />
      <ContextMenuItem
        icon={Pen}
        onClick={() => {
          onOpenInMarkup(state.src)
          onClose()
        }}
      >
        Mark up image…
      </ContextMenuItem>
    </ContextMenu>
  )
}

function filenameFromSrc(src: string, alt?: string): string {
  if (alt && /\.(png|jpe?g|gif|webp)$/i.test(alt)) return alt
  try {
    const url = new URL(src, window.location.origin)
    const last = url.pathname.split('/').filter(Boolean).pop() || ''
    if (/\.(png|jpe?g|gif|webp)$/i.test(last)) return last
  } catch {
    /* fallthrough */
  }
  return defaultFilenameFor('image/png')
}
